'use client';

import { useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';

import { useAuth } from './useAuth';
import { usePermissions } from './usePermissions';
import { useMcpConnectionsAccess } from './mcp-connections-access';
import { useFirstPermittedRoute } from './useFirstPermittedRoute';
import {
  getRequiredPermission,
  isRouteExempt,
  isRouteUnionGated,
  UNION_GATED_ROUTE,
} from '@/shared/constants/route-permissions';

const ACCESS_DENIED_ROUTE = '/access-denied';

interface RouteGuardState {
  isAuthorized: boolean;
  isChecking: boolean;
}

/**
 * The Surface's access decision for one route.
 *
 * **The default is CLOSED**: a route with no mapped permission is DENIED unless
 * it appears in `ROUTE_EXEMPTIONS`. This inverts what the guard used to do —
 * `!requiredPermission ||` admitted every unmapped route — and requirements
 * declared the closed default for this Surface on 2026-08-25, under the same
 * Explicit Grant Model that governs permissions themselves: what is not listed
 * is denied.
 *
 * The screens that are meant to be reachable without a code have not changed;
 * they are exempt EXPLICITLY instead of by omission. What changes is the next
 * screen somebody adds without a code: it is now a lock-out somebody reports on
 * the first day, rather than an opening nobody granted and nobody sees.
 *
 * One route is answered by the gateway rather than by the active role's codes:
 * `UNION_GATED_ROUTE` (the MCP console), which follows the union of the user's
 * roles — see its doc in `route-permissions.ts`. It is also the LAST fallback
 * of a denied route (`useFirstPermittedRoute`, AB#28616).
 */
function allows(
  pathname: string,
  permissions: string[],
  mcpAllowed: boolean
): boolean {
  if (isRouteUnionGated(pathname)) return mcpAllowed;
  const requiredPermission = getRequiredPermission(pathname);
  if (requiredPermission) {
    return permissions.includes(requiredPermission);
  }
  return isRouteExempt(pathname);
}

/**
 * Where a denied route sends the user: the fallback route (MCP included,
 * AB#28616), else Access Denied. Never back to the MCP console from the MCP
 * console: a denial there means the gateway said no, so the redirect would
 * loop. The guard and the fallback read the same query and agree today; this
 * does not rely on it.
 */
function deniedRedirectTarget(
  pathname: string,
  fallbackRoute: string | undefined
): string {
  if (fallbackRoute === undefined) return ACCESS_DENIED_ROUTE;
  if (fallbackRoute === UNION_GATED_ROUTE && isRouteUnionGated(pathname)) {
    return ACCESS_DENIED_ROUTE;
  }
  return fallbackRoute;
}

export function useRouteGuard(): RouteGuardState {
  const pathname = usePathname();
  const router = useRouter();
  const { isAuthenticated } = useAuth();
  const { permissions, isLoading: permissionsLoading } = usePermissions();
  const { allowed: mcpAllowed, isLoading: mcpAccessLoading } =
    useMcpConnectionsAccess();
  // Where a DENIED route goes. It waits for the MCP answer only when the
  // active role maps no route at all (AB#28616).
  const { route: fallbackRoute, isLoading: fallbackLoading } =
    useFirstPermittedRoute();
  // The access read only matters on the route it gates; elsewhere it must not
  // hold the whole shell behind a spinner.
  const isLoading =
    permissionsLoading || (isRouteUnionGated(pathname) && mcpAccessLoading);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    if (isLoading) return;
    // When not authenticated, skip permission check — AuthProvider handles redirect to login
    if (!isAuthenticated) return;

    if (allows(pathname, permissions, mcpAllowed)) {
      setIsChecking(false);
      return;
    }

    // Denied, and the fallback may be the MCP console: do not decide before
    // the gateway has answered, or a user whose only access is MCP would
    // flash through Access Denied.
    if (fallbackLoading) return;
    router.replace(deniedRedirectTarget(pathname, fallbackRoute));
  }, [
    pathname,
    permissions,
    mcpAllowed,
    isLoading,
    isAuthenticated,
    fallbackRoute,
    fallbackLoading,
    router,
  ]);

  const isAuthorized = allows(pathname, permissions, mcpAllowed);

  return {
    isAuthorized,
    isChecking:
      isLoading ||
      isChecking ||
      !isAuthenticated ||
      (!isAuthorized && fallbackLoading),
  };
}
